import {
  CategoryFieldName,
  CategoryName,
  ICategoryParsed,
  IContext,
  IInParamsValues,
  IShowCategoryInParamItemWithField,
  IViewFieldWithFieldParsed,
  IViewParsed,
  ShowCategoryInParams,
  TViewName,
} from '@infotech-soe/metaschema';

import CachedSchemaRepository from '../../../infrastructure/schemas/storage/CachedSchemaRepository';
import {
  isShowCategoryArgWithContext,
  isShowCategoryArgWithField,
  isShowCategoryArgWithInParam,
  isShowCategoryArgWithState,
  isShowCategoryArgWithValue,
} from '../../../infrastructure/schemas/typeGuards/showCategory';
import { DatasetGridRow } from '../../dataset/view-model/DatasetGridRow';

const enum SourceType {
  CATEGORY = 'category',
  VIEW = 'view',
}

interface CategorySource {
  type: SourceType.CATEGORY;
  schema: ICategoryParsed;
}

interface ViewSource {
  type: SourceType.VIEW;
  schema: IViewParsed;
}

type DatasetSource = CategorySource | ViewSource;

const getViewFieldBySource = async (
  source: ViewSource,
  inParamValue: IShowCategoryInParamItemWithField
): Promise<CategoryFieldName | undefined> => {
  let viewField;

  for (const [field, descr] of Object.entries(source.schema.Fields)) {
    if ((descr as IViewFieldWithFieldParsed).field === inParamValue.field) {
      viewField = field;
      break;
    }
  }

  return viewField;
};

const getCategoryFieldBySource = async (
  source: CategorySource,
  inParamValue: IShowCategoryInParamItemWithField
): Promise<CategoryFieldName | undefined> =>
  source.schema[inParamValue.field] ? inParamValue.field : undefined;

const getDatasetSource = async (
  source: CategoryName | TViewName
): Promise<DatasetSource> => {
  const schemaRepository = new CachedSchemaRepository();
  try {
    const viewSchema = await schemaRepository.getView(source);
    return {
      type: SourceType.VIEW,
      schema: viewSchema,
    } as ViewSource;
  } catch {
    const categorySchema = await schemaRepository.getCategory(source);
    return {
      type: SourceType.CATEGORY,
      schema: categorySchema,
    } as CategorySource;
  }
};

const getField = async (
  datasetSource: CategoryName | TViewName,
  inParamValue: IShowCategoryInParamItemWithField
): Promise<CategoryFieldName | undefined> => {
  const source = await getDatasetSource(datasetSource);

  if (source.type === SourceType.VIEW) {
    return getViewFieldBySource(source, inParamValue);
  }

  return getCategoryFieldBySource(source, inParamValue);
};

export const processShowCategoryInParams = async (
  context: IContext,
  InParams: ShowCategoryInParams,
  datasetSource: CategoryName | TViewName | undefined,
  record: DatasetGridRow,
  processState: Record<string, any> | undefined
): Promise<IInParamsValues> => {
  const inParams: IInParamsValues = {};

  for (const [inParamName, inParamValue] of Object.entries(InParams)) {
    if (isShowCategoryArgWithInParam(inParamValue)) {
      inParams[inParamName] = context.InParams?.[inParamValue.inParam];
    } else if (isShowCategoryArgWithContext(inParamValue)) {
      inParams[inParamName] = context[inParamValue.context];
    } else if (
      datasetSource &&
      isShowCategoryArgWithField(inParamValue) &&
      context.Id
    ) {
      const field: CategoryFieldName | undefined = await getField(
        datasetSource,
        inParamValue
      );

      if (field && record) {
        inParams[inParamName] = record[field];
      }
    } else if (isShowCategoryArgWithState(inParamValue) && processState) {
      inParams[inParamName] = processState[inParamValue.fromState];
    } else if (isShowCategoryArgWithValue(inParamValue)) {
      inParams[inParamName] = inParamValue.value;
    }
  }

  return inParams;
};
